const TTL_MS = 3600000;
const STORAGE_PREFIX = 'cloakEgms:v1:';

export type CloakCacheEntry = {
  mnums: string[];
  fetchedAt: number;
};

type CloakCacheListener = (casinoId: string) => void;

const listeners = new Set<CloakCacheListener>();
const inflight = new Map<string, Promise<string[]>>();

function storageKey(casinoId: string): string {
  return `${STORAGE_PREFIX}${casinoId}`;
}

function readEntry(casinoId: string): CloakCacheEntry | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey(casinoId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CloakCacheEntry;
    if (!parsed || !Array.isArray(parsed.mnums) || typeof parsed.fetchedAt !== 'number') {
      return null;
    }
    if (Date.now() - parsed.fetchedAt > TTL_MS) return null;
    return {
      mnums: parsed.mnums.map((m) => String(m)),
      fetchedAt: parsed.fetchedAt,
    };
  } catch {
    return null;
  }
}

function writeEntry(casinoId: string, mnums: string[]): void {
  if (typeof window === 'undefined') return;
  const entry: CloakCacheEntry = { mnums, fetchedAt: Date.now() };
  try {
    window.localStorage.setItem(storageKey(casinoId), JSON.stringify(entry));
  } catch {
    /* quota / private mode */
  }
}

export function bustCloakCache(casinoId: string): void {
  const id = String(casinoId || '').trim();
  if (!id) return;
  inflight.delete(id);
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem(storageKey(id));
    } catch {
      /* ignore */
    }
  }
  listeners.forEach((cb) => cb(id));
}

export function subscribeCloakCache(cb: CloakCacheListener): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export async function getCloakedMnums(
  casinoId: string,
  fetcher: () => Promise<string[]>
): Promise<string[]> {
  const id = String(casinoId || '').trim();
  if (!id) return [];
  const cached = readEntry(id);
  if (cached) return cached.mnums;
  const existing = inflight.get(id);
  if (existing) return existing;
  const pending = (async () => {
    const raw = await fetcher();
    const mnums = (Array.isArray(raw) ? raw : []).map((m) => String(m));
    writeEntry(id, mnums);
    return mnums;
  })();
  inflight.set(id, pending);
  try {
    return await pending;
  } finally {
    inflight.delete(id);
  }
}
